import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { OptionType } from "@models/clients/optionType";

export interface DropdownOptionsCache {
    [key: string]: any[];
}

/** Stored cache shape: version from backend + data. Old cache may be plain data (no version). */
interface CachedOptionsPayload {
    version?: string | null;
    data?: DropdownOptionsCache;
}

export class DropdownOptionsService {
    static $inject = ["clientCRMRepo", "$q"];
    
    private readonly CACHE_KEY = 'dropdownOptionsCache';
    private optionsCache: DropdownOptionsCache | null = null;
    /** Version string from backend when we last saved the cache; used to decide if we can use localStorage. */
    private cachedVersion: string | null = null;
    private loadingPromise: angular.IPromise<DropdownOptionsCache> | null = null;

    constructor(
        private clientCRMRepo: ClientCRMRepo,
        private $q: angular.IQService
    ) {
        this.loadFromCache();
    }

    /**
     * Get all dropdown options, loading from cache or backend as needed.
     * When we have cache we always check the backend version; if it changed we refetch so options stay fresh (e.g. after config changes).
     */
    public getAllOptions(): angular.IPromise<DropdownOptionsCache> {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        if (this.optionsCache) {
            this.loadingPromise = this.checkVersionAndMaybeRefetch();
            this.loadingPromise.finally(() => {
                this.loadingPromise = null;
            });
            return this.loadingPromise;
        }

        this.loadingPromise = this.loadAllOptionsFromBackend();
        this.loadingPromise.finally(() => {
            this.loadingPromise = null;
        });
        return this.loadingPromise;
    }

    /**
     * If backend version matches cached version, use cache. Otherwise clear and refetch from backend.
     */
    private checkVersionAndMaybeRefetch(): angular.IPromise<DropdownOptionsCache> {
        return this.clientCRMRepo
            .getOptionsVersion()
            .then((res: angular.IHttpResponse<{ version: string | null }>) => {
                const serverVersion = (res.data && res.data.version) != null ? (res.data && res.data.version) : null;
                if (serverVersion === this.cachedVersion && this.optionsCache) {
                    return this.optionsCache;
                }
                this.clearCache();
                return this.loadAllOptionsFromBackend();
            })
            .catch(() => {
                if (this.optionsCache) {
                    return this.optionsCache;
                }
                return this.loadAllOptionsFromBackend();
            });
    }

    /**
     * Get specific options by type
     */
    public getOptions(optionType: string): angular.IPromise<any[]> {
        return this.getAllOptions().then(cache => {
            return cache[optionType] || [];
        });
    }

    /**
     * Get multiple options at once
     */
    public getMultipleOptions(optionTypes: string[]): angular.IPromise<{[key: string]: any[]}> {
        return this.getAllOptions().then(cache => {
            const result: {[key: string]: any[]} = {};
            optionTypes.forEach(type => {
                result[type] = cache[type] || [];
            });
            return result;
        });
    }

    /**
     * Load all options from backend in a single call, then persist with current backend version.
     */
    private loadAllOptionsFromBackend(): angular.IPromise<DropdownOptionsCache> {
        return this.clientCRMRepo
            .getAllOptions()
            .then((response: any) => {
                const optionsData = response.data || {};
                const cache: DropdownOptionsCache = { ...optionsData };
                this.optionsCache = cache;
                return this.clientCRMRepo.getOptionsVersion().then(
                    (vRes: angular.IHttpResponse<{ version: string | null }>) => {
                        const version = (vRes.data && vRes.data.version) != null ? (vRes.data && vRes.data.version) : null;
                        this.cachedVersion = version;
                        this.saveToCache(cache, version);
                        return cache;
                    },
                    () => {
                        this.saveToCache(cache, null);
                        return cache;
                    }
                );
            })
            .catch((error: any) => {
                console.error('Failed to load dropdown options from backend:', error);
                const emptyCache: DropdownOptionsCache = {};
                this.optionsCache = emptyCache;
                return this.$q.resolve(emptyCache);
            });
    }


    /**
     * Load options from localStorage. Supports versioned payload { version, data } or legacy plain object.
     */
    private loadFromCache(): void {
        try {
            const raw = localStorage.getItem(this.CACHE_KEY);
            if (!raw) return;
            const parsed: CachedOptionsPayload | DropdownOptionsCache = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && 'data' in parsed && parsed.data) {
                const payload = parsed as CachedOptionsPayload;
                this.optionsCache = payload.data as DropdownOptionsCache;
                this.cachedVersion = payload.version != null ? payload.version : null;
            } else {
                this.optionsCache = Array.isArray(parsed) ? {} : (parsed as DropdownOptionsCache);
                this.cachedVersion = null;
            }
        } catch (error) {
            console.warn('Failed to load dropdown options from cache:', error);
            this.optionsCache = null;
            this.cachedVersion = null;
        }
    }

    /**
     * Save options to localStorage with backend version for future invalidation.
     */
    private saveToCache(cache: DropdownOptionsCache, version: string | null): void {
        try {
            localStorage.setItem(
                this.CACHE_KEY,
                JSON.stringify({ version, data: cache })
            );
        } catch (error) {
            console.warn('Failed to save dropdown options to cache:', error);
        }
    }


    /**
     * Clear the cache (useful for testing or when data needs to be refreshed)
     */
    public clearCache(): void {
        this.optionsCache = null;
        this.cachedVersion = null;
        try {
            localStorage.removeItem(this.CACHE_KEY);
        } catch (error) {
            console.warn('Failed to clear dropdown options cache:', error);
        }
    }

    /**
     * Force refresh options from backend
     */
    public refreshOptions(): angular.IPromise<DropdownOptionsCache> {
        this.clearCache();
        return this.getAllOptions();
    }

    /**
     * Check if options are currently loading
     */
    public isLoading(): boolean {
        return this.loadingPromise !== null;
    }

    /**
     * Get cache status information
     */
    public getCacheStatus(): {isValid: boolean} {
        return {
            isValid: this.optionsCache !== null
        };
    }
}



// WEBPACK FOOTER //
// ./src/services/clients/dropdownOptions.service.ts